import { Select } from "@bafe/ui";
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, Columns3 } from "lucide-react";
import { ChevronDown } from "reicon-react";
import { useEffect, type ReactNode, type RefObject } from "react";

import { closeSiblingSelectMenus } from "../../lib/select-menu.js";
import { HUB_OPERATION_PAGE_SIZES, type HubOperationPageSize } from "./hub-operation-pagination.js";

export type HubFilterChoice = readonly [string, string] | readonly [string, string, number];

export function HubOperationScreen({ children }: { children: ReactNode }) {
	return <div className="task-screen">{children}</div>;
}

export function HubOperationBar({ sub, children }: { sub: string; children?: ReactNode }) {
	return (
		<div className="row">
			<p className="sub">{sub}</p>
			{children ? <div className="actions">{children}</div> : null}
		</div>
	);
}

export function HubOperationEmpty({ children }: { children: ReactNode }) {
	return <p className="empty">{children}</p>;
}

export function HubOperationFormField({
	label,
	htmlFor,
	children,
}: {
	label: string;
	htmlFor?: string;
	children: ReactNode;
}) {
	return (
		<div className="hub-inventory-form-field flex min-w-0 flex-col gap-1.5">
			{htmlFor ? <label htmlFor={htmlFor}>{label}</label> : <span className="text-[13px] font-medium">{label}</span>}
			{children}
		</div>
	);
}

export function HubOperationFormStack({ children }: { children: ReactNode }) {
	return <div className="hub-inventory-asset-form flex flex-col gap-4">{children}</div>;
}

export function HubOperationFormRow({ children }: { children: ReactNode }) {
	return <div className="grid two hub-inventory-form-row">{children}</div>;
}

export function HubOperationModal({
	open,
	title,
	error,
	pending,
	action = "Save",
	hubForm = false,
	readOnly = false,
	onEdit,
	onClose,
	onSubmit,
	children,
}: {
	open: boolean;
	title: string;
	error: string;
	pending: boolean;
	action?: string;
	hubForm?: boolean;
	readOnly?: boolean;
	onEdit?: () => void;
	onClose: () => void;
	onSubmit: (form: FormData) => void;
	children: ReactNode;
}) {
	if (!open) return null;
	return (
		<dialog className="modal" open onClose={onClose}>
			<form
				className={hubForm ? "hub-inventory-asset-modal-form flex flex-col gap-4" : "grid"}
				onSubmit={(event) => {
					event.preventDefault();
					if (readOnly) return;
					onSubmit(new FormData(event.currentTarget));
				}}
			>
				<h2 className={hubForm ? "mb-0 shrink-0" : undefined}>{title}</h2>
				{children}
				{error ? <p className="err">{error}</p> : null}
				<div className={hubForm ? "mt-2 flex justify-end gap-2" : "actions"}>
					<button className="btn ghost" type="button" onClick={onClose}>{readOnly ? "Close" : "Cancel"}</button>
					{readOnly && onEdit ? (
						<button className="btn" type="button" onClick={onEdit}>Edit</button>
					) : null}
					{readOnly ? null : (
						<button className="btn" type="submit" disabled={pending}>{action}</button>
					)}
				</div>
			</form>
		</dialog>
	);
}

export function HubOperationFilterMenu({
	label,
	value,
	ariaLabel,
	items,
	onPick,
	active = Boolean(value),
	counted = false,
}: {
	label: string;
	value: string;
	ariaLabel: string;
	items: HubFilterChoice[];
	onPick: (next: string) => void;
	active?: boolean;
	counted?: boolean;
}) {
	const current = items.find(([id]) => id === value)?.[1]
		?? (value || items[0]?.[1] || label);
	return (
		<details
			className={["select", "task-filter-menu", active ? "is-on" : ""].filter(Boolean).join(" ")}
			onToggle={(event) => closeSiblingSelectMenus(event.currentTarget)}
			onPointerDown={(event) => event.stopPropagation()}
		>
			<summary aria-label={ariaLabel}>
				<span className="task-filter-k">{label}</span>
				<span className="task-filter-v">{current}</span>
				<ChevronDown size={12} aria-hidden="true" />
			</summary>
			<div className="select-pop" role="listbox">
				{items.map((item) => {
					const [id, text, count] = item;
					return (
						<button
							key={id || "all"}
							type="button"
							role="option"
							aria-selected={id === value}
							aria-label={counted && count != null ? `${text} · ${count}` : text}
							className={id === value ? "select-item active" : "select-item"}
							onClick={(event) => {
								onPick(id);
								event.currentTarget.closest("details")?.removeAttribute("open");
							}}
						>
							<span>{text}</span>
							{count != null ? <span className="task-filter-n" aria-hidden="true">{count}</span> : null}
						</button>
					);
				})}
			</div>
		</details>
	);
}

const SEARCH_ICON = (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<circle cx="11" cy="11" r="7" />
		<path d="m20 20-3.5-3.5" />
	</svg>
);

export function HubOperationSearchField({
	id,
	ariaLabel,
	placeholder,
	searchOpen,
	searchQuery,
	searchRef,
	onQueryChange,
	onEscape,
}: {
	id: string;
	ariaLabel: string;
	placeholder: string;
	searchOpen: boolean;
	searchQuery: string;
	searchRef: RefObject<HTMLInputElement | null>;
	onQueryChange: (value: string) => void;
	onEscape: () => void;
}) {
	return (
		<div className={searchOpen ? "doc-search-wrap is-open" : "doc-search-wrap"}>
			<div className="doc-search-field">
				<label className="sr" htmlFor={id}>{ariaLabel}</label>
				<input
					ref={searchRef}
					id={id}
					className="doc-search-input"
					type="search"
					placeholder={placeholder}
					aria-label={ariaLabel}
					value={searchQuery}
					tabIndex={searchOpen ? 0 : -1}
					onChange={(event) => onQueryChange(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === "Escape") onEscape();
					}}
				/>
			</div>
		</div>
	);
}

export function HubOperationSearchToggle({
	searchOpen,
	searchActive,
	ariaLabel,
	onToggle,
}: {
	searchOpen: boolean;
	searchActive: boolean;
	ariaLabel: string;
	onToggle: () => void;
}) {
	return (
		<button
			type="button"
			className={searchOpen || searchActive ? "active" : ""}
			aria-label={ariaLabel}
			aria-expanded={searchOpen}
			onClick={onToggle}
		>
			{SEARCH_ICON}
		</button>
	);
}

export function HubOperationToolbar({
	searchSlot,
	tools,
	primaryAction,
}: {
	searchSlot: ReactNode;
	tools?: ReactNode;
	primaryAction?: ReactNode;
}) {
	return (
		<>
			{searchSlot}
			<nav className="seg task-view" aria-label="List tools">
				{tools}
			</nav>
			{primaryAction}
		</>
	);
}

export function HubOperationColumnMenu<T extends string>({
	columns,
	visibility,
	onToggle,
	popClassName = "hub-inventory-columns-pop",
}: {
	columns: Array<{ id: T; label: string }>;
	visibility: Record<T, boolean>;
	onToggle: (id: T, checked: boolean) => void;
	popClassName?: string;
}) {
	const columnsFiltered = columns.some((column) => !visibility[column.id]);
	return (
		<details
			className={[
				"select",
				"task-filter-menu",
				"task-view-tag-filter",
				columnsFiltered ? "is-on" : "",
			].filter(Boolean).join(" ")}
			onToggle={(event) => closeSiblingSelectMenus(event.currentTarget)}
			onPointerDown={(event) => event.stopPropagation()}
		>
			<summary aria-label="Choose columns" title="Choose columns">
				<Columns3 size={16} aria-hidden="true" />
				<span className="sr-only">Columns</span>
			</summary>
			<div className={`select-pop ${popClassName}`}>
				{columns.map((column) => (
					<label key={column.id} className="select-item hub-inventory-column-item">
						<input
							type="checkbox"
							checked={visibility[column.id]}
							onChange={() => {
								const next = !visibility[column.id];
								const visibleCount = Object.values(visibility).filter(Boolean).length;
								if (!next && visibleCount <= 1) return;
								onToggle(column.id, next);
							}}
						/>
						<span>{column.label}</span>
					</label>
				))}
			</div>
		</details>
	);
}

export function HubOperationFilterClear({ onClear }: { onClear: () => void }) {
	return (
		<button className="task-filter-clear" type="button" onClick={onClear} aria-label="Clear filters">
			Clear
		</button>
	);
}

export function HubOperationTableEmptyRow({
	colSpan,
	children,
}: {
	colSpan: number;
	children: ReactNode;
}) {
	return (
		<tr className="hub-inventory-table-empty-row">
			<td colSpan={colSpan} className="hub-inventory-table-empty-cell">
				<p className="empty m-0">{children}</p>
			</td>
		</tr>
	);
}

export function hubOperationGroupedHasRows<T>(groupedRows: Array<[string, T[]]>): boolean {
	return groupedRows.some(([, items]) => items.length > 0);
}

export function HubOperationGroupHeader({
	label,
	collapsed,
	colSpan,
	onToggle,
}: {
	label: string;
	collapsed: boolean;
	colSpan: number;
	onToggle: () => void;
}) {
	return (
		<tr className="task-group-row hub-inventory-group-row">
			<th scope="rowgroup" colSpan={colSpan}>
				<button
					type="button"
					className={`hub-inventory-group-toggle${collapsed ? " is-collapsed" : ""}`}
					aria-expanded={!collapsed}
					onClick={onToggle}
				>
					<ChevronDown size={14} className="task-table-group-chevron" aria-hidden="true" />
					<span className="task-group-head">
						<span className="task-group-name">{label}</span>
					</span>
				</button>
			</th>
		</tr>
	);
}

export function useHubOperationSearchFocus(searchOpen: boolean, searchRef: RefObject<HTMLInputElement | null>) {
	useEffect(() => {
		if (searchOpen) searchRef.current?.focus();
	}, [searchOpen, searchRef]);
}

export function hubTableSortIndicator(sortKey: string, columnId: string, sortDir: "asc" | "desc"): string {
	if (sortKey !== columnId) return "";
	return sortDir === "asc" ? " ↑" : " ↓";
}

export type HubSortDir = "asc" | "desc";

export function toggleHubSort(
	currentKey: string,
	currentDir: HubSortDir,
	nextKey: string,
): { sortKey: string; sortDir: HubSortDir } {
	if (currentKey === nextKey) {
		return { sortKey: nextKey, sortDir: currentDir === "asc" ? "desc" : "asc" };
	}
	return { sortKey: nextKey, sortDir: "asc" };
}

export function HubOperationTablePagination({
	total,
	page,
	pageSize,
	totalPages,
	onPage,
	onPageSize,
	totalNoun = "records",
}: {
	total: number;
	page: number;
	pageSize: HubOperationPageSize;
	totalPages: number;
	onPage: (page: number) => void;
	onPageSize: (size: HubOperationPageSize) => void;
	totalNoun?: string;
}) {
	const atFirst = page <= 1;
	const atLast = page >= totalPages;
	return (
		<div
			className="registry-pagination flex flex-col gap-2 pt-1 text-xs sm:flex-row sm:items-center sm:justify-between"
			aria-label="Table pagination"
		>
			<p className="registry-pagination-total m-0 text-[var(--muted)]">
				{total} total {totalNoun}
			</p>
			<div className="registry-pagination-controls flex flex-wrap items-center gap-3 sm:justify-end">
				<label className="registry-pagination-page-size text-[var(--muted)]">
					<span>Rows per page</span>
					<Select
						className="registry-pagination-select"
						panelClassName="registry-pagination-select-panel"
						matchWidth={false}
						aria-label="Rows per page"
						value={String(pageSize)}
						onChange={(event) => {
							const next = Number(event.target.value);
							if ((HUB_OPERATION_PAGE_SIZES as readonly number[]).includes(next)) {
								onPageSize(next as HubOperationPageSize);
							}
						}}
					>
						{HUB_OPERATION_PAGE_SIZES.map((size) => (
							<option key={size} value={size}>{size}</option>
						))}
					</Select>
				</label>
				<span className="registry-pagination-page whitespace-nowrap text-[var(--text)]">
					Page <span className="font-semibold">{page}</span> of <span className="font-semibold">{totalPages}</span>
				</span>
				<div className="registry-pagination-nav flex items-center gap-1">
					<button
						type="button"
						className="registry-pagination-nav-btn"
						disabled={atFirst}
						aria-label="First page"
						onClick={() => onPage(1)}
					>
						<ChevronFirst size={16} aria-hidden="true" />
					</button>
					<button
						type="button"
						className="registry-pagination-nav-btn"
						disabled={atFirst}
						aria-label="Previous page"
						onClick={() => onPage(page - 1)}
					>
						<ChevronLeft size={16} aria-hidden="true" />
					</button>
					<button
						type="button"
						className="registry-pagination-nav-btn"
						disabled={atLast}
						aria-label="Next page"
						onClick={() => onPage(page + 1)}
					>
						<ChevronRight size={16} aria-hidden="true" />
					</button>
					<button
						type="button"
						className="registry-pagination-nav-btn"
						disabled={atLast}
						aria-label="Last page"
						onClick={() => onPage(totalPages)}
					>
						<ChevronLast size={16} aria-hidden="true" />
					</button>
				</div>
			</div>
		</div>
	);
}

export function HubOperationListTable({ children }: { children: ReactNode }) {
	return (
		<div className="hub-operation-list-table compact-table-view flex min-w-0 flex-col gap-2">
			{children}
		</div>
	);
}
